'use strict';

const ONTARIO_BOUNDS = {
  north: 54,
  south: 41.36,
  west: -97.7,
  east: -73.4,
};

/**
 * Add the Google Maps script tag and loads the API
 * @returns The maps API (window.google.maps)
 */
function loadApi() {
  return new Promise<typeof google.maps>((resolve, reject) => {
    if (!mapsExists()) {
      // console.debug('loading maps');
      const script = document.createElement('script');

      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve(window.google.maps as typeof google.maps);
      };

      script.onerror = reject;
      script.onabort = reject;

      document.head.appendChild(script);
    } else {
      // console.debug('already have maps');
      resolve(window.google.maps as typeof google.maps);
    }
  });
}

/**
 * This is called from inside client JS, and contains all of
 * your maps API logic
 * @param styles Snazzy Maps styles json file
 */
export async function initMaps(styles: any[] = []) {
  let mapsApi: typeof google.maps;
  try {
    mapsApi = await loadApi();
    // console.debug('loaded maps');
  } catch (err) {
    throw new Error('Could not load Google Maps API');
  }

  // ---------------
  //  Your maps logic starts here:
  // ---------

  let mapContainers = document.querySelectorAll('.js-map-container') as NodeListOf<HTMLElement>;

  mapContainers.forEach((element) => {
    initMap(element);
  });

  // @ts-ignore
  let map: google.maps.Map;

  function initMap(mapElement: HTMLElement) {
    const mapContainer = mapElement.querySelector('.js-map') as HTMLElement;

    const lon = Number(mapContainer.getAttribute('data-lon'));
    const lat = Number(mapContainer.getAttribute('data-lat'));
    const zoomDesktop = mapContainer.getAttribute('data-zoom-desktop')
      ? Number(mapContainer.getAttribute('data-zoom-desktop'))
      : 11;
    const zoomMobile = mapContainer.getAttribute('data-zoom-mobile')
      ? Number(mapContainer.getAttribute('data-zoom-mobile'))
      : 11;

    let windowWidth = window.innerWidth;
    let zoomLevel = (windowWidth > 767)
      ? zoomDesktop
      : zoomMobile;

    let mapOptions = {
      zoom: zoomLevel,
      center: { lat: lat, lng: lon },
      mapTypeId: 'roadmap',
      disableDefaultUI: true,
      zoomControl: false,
      styles,
      restriction: {
        latLngBounds: ONTARIO_BOUNDS,
        strictBounds: false,
      },
    };

    map = new mapsApi.Map(mapContainer, mapOptions);

    new mapsApi.Marker({
      position: { lat: lat, lng: lon },
      map: map,
      icon: {
        url: '/static/img/ui_map-marker.svg',
        scaledSize: new google.maps.Size(35, 48),
      },
    });
  }
}

/**
 * Check if the Google Maps API has been loaded
 * @returns {boolean} If the Google Maps API has been loaded or not
 */
export function mapsExists() {
  return typeof window.google === 'object' && typeof window.google.maps === 'object';
}
